import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { initializePaddle, type Paddle, type Environments, type CheckoutEventsData } from '@paddle/paddle-js';

type CheckoutEventHandler = (data: CheckoutEventsData) => void;

interface PaddleContextType {
  paddle: Paddle | null;
  isReady: boolean;
  onCheckoutComplete: (handler: CheckoutEventHandler) => void;
  offCheckoutComplete: (handler: CheckoutEventHandler) => void;
}

const PaddleContext = createContext<PaddleContextType>({
  paddle: null,
  isReady: false,
  onCheckoutComplete: () => {},
  offCheckoutComplete: () => {},
});

export const usePaddle = (): PaddleContextType => useContext(PaddleContext);

interface PaddleProviderProps {
  children: ReactNode;
}

export const PaddleProvider: React.FC<PaddleProviderProps> = ({ children }) => {
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const completionHandlers = useRef<Set<CheckoutEventHandler>>(new Set());

  const onCheckoutComplete = useCallback((handler: CheckoutEventHandler) => {
    completionHandlers.current.add(handler);
  }, []);

  const offCheckoutComplete = useCallback((handler: CheckoutEventHandler) => {
    completionHandlers.current.delete(handler);
  }, []);

  useEffect(() => {
    const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
    if (!token) return;

    const environment: Environments =
      import.meta.env.VITE_PADDLE_ENVIRONMENT === 'live' ? 'production' : 'sandbox';

    initializePaddle({
      environment,
      token,
      eventCallback: (event) => {
        if (event.name === 'checkout.completed' && event.data) {
          completionHandlers.current.forEach((handler) => handler(event.data!));
        }
      },
    }).then((paddleInstance) => {
      if (paddleInstance) setPaddle(paddleInstance);
    });
  }, []);

  return (
    <PaddleContext.Provider value={{ paddle, isReady: !!paddle, onCheckoutComplete, offCheckoutComplete }}>
      {children}
    </PaddleContext.Provider>
  );
};
